import { Plus, X } from "lucide-react";
import type { DeviceTypeFieldDefinition } from "../../api/types";
import { TagListInput } from "./TagListInput";
import {
  configFieldId,
  countObjectArrayValues,
  emptyObjectArrayItem,
  serializedObjectArrayBytes,
  type ObjectArrayItemDraft,
} from "./configForm";

// Editor for an `object_array` field: a list of entries, each rendered from the
// field's item schema (text inputs, one-per-line lists, numbers, checkboxes,
// dropdowns). Schema-driven like ConfigFieldsForm, so the item shape the form
// produces is the one the backend validates. Draft <-> value conversion lives
// in ./configForm (objectArrayDraft / objectArrayValue).

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white";

export interface ObjectArrayFieldProps {
  field: DeviceTypeFieldDefinition;
  items: ObjectArrayItemDraft[];
  onChange: (items: ObjectArrayItemDraft[]) => void;
  idPrefix?: string;
  // What to call one item in buttons and headings ("sensor entry").
  itemNoun?: string;
  // Optional per-value check (e.g. MAC format). Called with a whole field's
  // raw value for the message under the control, and with each value of a
  // list field to mark that chip. Returns a warning, or null.
  itemWarning?: (itemKey: string, raw: string) => string | null;
  // Optional item key rendered inline in each item's header row (e.g. the
  // entry's label) instead of in the field grid.
  headerFieldPath?: string;
}

const sectionClass =
  "rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-riot-navy-700 dark:bg-riot-navy-900/60";

export function ObjectArrayField({
  field,
  items,
  onChange,
  idPrefix = "device-config",
  itemNoun = "entry",
  itemWarning,
  headerFieldPath,
}: ObjectArrayFieldProps) {
  const itemFields = field.itemFields ?? [];
  const headerField = headerFieldPath ? itemFields.find((f) => f.path === headerFieldPath) : undefined;
  const bodyFields = headerField ? itemFields.filter((f) => f !== headerField) : itemFields;
  const total = countObjectArrayValues(field, items);
  const cap = field.maxTotalValues;
  const byteCap = field.maxSerializedBytes;
  const bytes = byteCap ? serializedObjectArrayBytes(field, items) : 0;
  const atItemCap = field.maxItems !== undefined && field.maxItems > 0 && items.length >= field.maxItems;

  function updateItem(index: number, key: string, value: string) {
    onChange(items.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  }

  function removeItem(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function addItem() {
    onChange([...items, emptyObjectArrayItem(field)]);
  }

  return (
    <section className={sectionClass}>
      <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-riot-navy-300">{field.label}</h4>
      {field.description && <p className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">{field.description}</p>}

      {/* Capacity: identifiers and shadow bytes on one line. */}
      {((cap !== undefined && cap > 0) || (byteCap !== undefined && byteCap > 0)) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
          {cap !== undefined && cap > 0 && (
            <span className={total > cap ? "font-medium text-red-600 dark:text-red-400" : "text-gray-500 dark:text-riot-navy-400"}>
              {total} of {cap} identifiers used
              {total > cap ? ` — remove ${total - cap} before saving` : ""}
            </span>
          )}
          {byteCap !== undefined && byteCap > 0 && (
            <span
              data-testid={`${idPrefix}-bytes`}
              className={bytes > byteCap ? "font-medium text-red-600 dark:text-red-400" : "text-gray-500 dark:text-riot-navy-400"}
            >
              {bytes} / {byteCap} bytes
              {bytes > byteCap
                ? " — too large for the device shadow: remove entries or shorten labels and names before saving"
                : ""}
            </span>
          )}
        </div>
      )}

      <div className="mt-3 space-y-3">
        {items.length === 0 && (
          <p className="rounded-md border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500 dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-riot-navy-400">
            Nothing configured yet. Use &ldquo;Add {itemNoun}&rdquo; to start.
          </p>
        )}

        {items.map((item, index) => (
          <div
            key={index}
            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-950"
            data-testid={`${idPrefix}-item-${index}`}
          >
            {/* On phones the label input drops to its own full-width row under the heading. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-riot-navy-300">
                {itemNoun} {index + 1}
              </span>
              {headerField ? (
                <input
                  id={configFieldId(idPrefix, `${field.path}-${index}-${headerField.path}`)}
                  type="text"
                  aria-label={headerField.label}
                  title={headerField.description}
                  value={item[headerField.path] ?? ""}
                  maxLength={headerField.max}
                  onChange={(e) => updateItem(index, headerField.path, e.target.value)}
                  placeholder={`${headerField.label} (optional)${headerField.placeholder ? `, e.g. ${headerField.placeholder}` : ""}`}
                  className={`${inputClass} order-last min-w-0 basis-full sm:order-none sm:basis-0 sm:flex-1`}
                />
              ) : (
                <span className="flex-1" />
              )}
              <button
                type="button"
                onClick={() => removeItem(index)}
                aria-label={`Remove ${itemNoun} ${index + 1}`}
                title={`Remove ${itemNoun}`}
                className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:border-riot-navy-600 dark:text-riot-navy-300 dark:hover:bg-riot-navy-700 dark:hover:text-red-300"
              >
                <X size={14} />
              </button>
            </div>
            {headerField?.description && (
              <p className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">{headerField.description}</p>
            )}

            {bodyFields.length > 0 && (
              <div className={`mt-3 grid grid-cols-1 gap-4 ${bodyFields.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                {bodyFields.map((itemField) => {
                  const id = configFieldId(idPrefix, `${field.path}-${index}-${itemField.path}`);
                  const raw = item[itemField.path] ?? "";
                  const warning = itemWarning ? itemWarning(itemField.path, raw) : null;
                  return (
                    <div key={itemField.path}>
                      {itemField.kind === "boolean" ? (
                        <label htmlFor={id} className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                          <input
                            id={id}
                            type="checkbox"
                            checked={raw === "true"}
                            onChange={(e) => updateItem(index, itemField.path, e.target.checked ? "true" : "false")}
                            className="h-4 w-4 rounded border-gray-300 text-riot-gold-500 focus:ring-riot-gold-500"
                          />
                          {itemField.label}
                        </label>
                      ) : (
                        <>
                          <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                            {itemField.label}
                          </label>
                          <ItemControl
                            field={itemField}
                            id={id}
                            value={raw}
                            onChange={(value) => updateItem(index, itemField.path, value)}
                            invalid={itemWarning ? (tag) => itemWarning(itemField.path, tag) : undefined}
                          />
                        </>
                      )}
                      {itemField.description && (
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">{itemField.description}</p>
                      )}
                      {warning && (
                        <p role="alert" className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400">
                          {warning}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addItem}
        disabled={atItemCap}
        className="mt-3 inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
      >
        <Plus size={14} /> Add {itemNoun}
      </button>
    </section>
  );
}

function ItemControl({
  field,
  id,
  value,
  onChange,
  invalid,
}: {
  field: DeviceTypeFieldDefinition;
  id: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: (tag: string) => string | null;
}) {
  if (field.kind === "string_array") {
    return (
      <TagListInput
        id={id}
        value={value}
        onChange={onChange}
        maxLength={field.max}
        invalid={invalid}
        placeholder={`${field.placeholder ? `e.g. ${field.placeholder}` : "Add a value"} — Enter to add`}
      />
    );
  }
  if (field.kind === "textarea") {
    return (
      <textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={field.placeholder} className={inputClass} />
    );
  }
  if (field.kind === "enum" && field.choices?.length) {
    return (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        <option value="">Select…</option>
        {field.choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
    );
  }
  if (field.kind === "number") {
    return (
      <input
        id={id}
        type="number"
        min={field.min}
        max={field.max}
        step={field.step ?? "any"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={inputClass}
      />
    );
  }
  return (
    <input
      id={id}
      type="text"
      value={value}
      maxLength={field.max}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      className={inputClass}
    />
  );
}
